/**
 * Prepares files for the media composer the way WhatsApp Web does: decides how each file goes
 * (photo, video, audio, document or sticker) with the shared rules, converts in the browser
 * what the Cloud API would refuse, and builds previews (image, video frame, PDF first page).
 */
import {
  HEIF_IMAGES,
  IMAGE_MAX_BYTES,
  STICKER_SIZE,
  STICKER_STATIC_MAX_BYTES,
  extensionOf,
  planUpload,
  resolveMime,
  type MediaKind,
  type UploadIntent,
  type UploadPlan,
} from "@desfecha/contracts";

export type PrepError =
  | "type_not_accepted"
  | "too_large"
  | "empty"
  | "conversion_failed"
  | "heic_browser";

export type PrepNote =
  "converted_jpeg" | "compressed" | "as_text" | "sticker" | null;

export interface PreparedFile {
  key: string;
  intent: UploadIntent;
  original: File;
  /** What gets uploaded: the original or its browser conversion. Null while preparing or on error. */
  file: File | null;
  plan: UploadPlan;
  kind: MediaKind | null;
  status: "preparing" | "ready" | "error";
  error: { code: PrepError; maxBytes?: number } | null;
  note: PrepNote;
  /** Object URL used by the large preview (converted image when there was a conversion). */
  previewUrl: string | null;
  /** Small image for the thumbnail strip: photo, video frame or PDF first page. */
  thumbUrl: string | null;
  pages: number | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  caption: string;
}

let sequence = 0;
const nextKey = () => `f${Date.now().toString(36)}${(sequence++).toString(36)}`;

/** Longest side of photos, as WhatsApp's HD quality. */
const PHOTO_MAX_SIDE = 4096;

function renamed(name: string, ext: string): string {
  const dot = name.lastIndexOf(".");
  return (dot > 0 ? name.slice(0, dot) : name || "arquivo") + "." + ext;
}

function toBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/** Decodes an image with the browser; SVG goes through an <img> because bitmaps refuse it. */
async function decodeImage(file: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch (err) {
    if (!file.type.includes("svg")) throw err;
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      return await createImageBitmap(img);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

/** Redraws an image as JPEG under the photo limit, shrinking only when quality alone is not enough. */
async function toJpeg(
  source: Blob,
  maxBytes = IMAGE_MAX_BYTES,
): Promise<{ blob: Blob; width: number; height: number }> {
  const bitmap = await decodeImage(source);
  try {
    let scale = Math.min(
      1,
      PHOTO_MAX_SIDE / Math.max(bitmap.width, bitmap.height),
    );
    for (let attempt = 0; attempt < 8; attempt++) {
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(bitmap, 0, 0, width, height);
      for (const quality of [0.9, 0.8, 0.7]) {
        const blob = await toBlob(canvas, "image/jpeg", quality);
        if (blob && blob.size <= maxBytes) return { blob, width, height };
      }
      scale *= 0.75;
    }
    throw new Error("conversion_failed");
  } finally {
    bitmap.close();
  }
}

/** A 512×512 transparent WebP, the image centred and fitted, under the sticker limit. */
async function toSticker(source: Blob): Promise<Blob> {
  const bitmap = await decodeImage(source);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = STICKER_SIZE;
    canvas.height = STICKER_SIZE;
    const ctx = canvas.getContext("2d")!;
    const scale = Math.min(
      STICKER_SIZE / bitmap.width,
      STICKER_SIZE / bitmap.height,
    );
    const w = bitmap.width * scale;
    const h = bitmap.height * scale;
    ctx.drawImage(bitmap, (STICKER_SIZE - w) / 2, (STICKER_SIZE - h) / 2, w, h);
    for (const quality of [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3]) {
      const blob = await toBlob(canvas, "image/webp", quality);
      if (
        blob &&
        blob.type === "image/webp" &&
        blob.size <= STICKER_STATIC_MAX_BYTES
      )
        return blob;
    }
    throw new Error("conversion_failed");
  } finally {
    bitmap.close();
  }
}

/** First frame (at 0.1 s) of a local video as a JPEG, plus its size and duration. */
async function videoFrame(url: string): Promise<{
  thumb: string | null;
  width: number;
  height: number;
  durationMs: number | null;
}> {
  const video = document.createElement("video");
  video.muted = true;
  video.preload = "metadata";
  video.playsInline = true;
  video.src = url;
  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error("video"));
      setTimeout(() => reject(new Error("timeout")), 8000);
    });
    const durationMs = Number.isFinite(video.duration)
      ? Math.round(video.duration * 1000)
      : null;
    await new Promise<void>((resolve) => {
      video.onseeked = () => resolve();
      video.currentTime = Math.min(0.1, (video.duration || 1) / 2);
      setTimeout(resolve, 1500);
    });
    const canvas = document.createElement("canvas");
    const side = Math.min(
      1,
      320 / Math.max(video.videoWidth || 1, video.videoHeight || 1),
    );
    canvas.width = Math.max(1, Math.round((video.videoWidth || 1) * side));
    canvas.height = Math.max(1, Math.round((video.videoHeight || 1) * side));
    canvas
      .getContext("2d")!
      .drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await toBlob(canvas, "image/jpeg", 0.8);
    return {
      thumb: blob ? URL.createObjectURL(blob) : null,
      width: video.videoWidth,
      height: video.videoHeight,
      durationMs,
    };
  } catch {
    return { thumb: null, width: 0, height: 0, durationMs: null };
  } finally {
    video.removeAttribute("src");
    video.load();
  }
}

async function audioDuration(url: string): Promise<number | null> {
  const audio = document.createElement("audio");
  audio.preload = "metadata";
  audio.src = url;
  try {
    await new Promise<void>((resolve, reject) => {
      audio.onloadedmetadata = () => resolve();
      audio.onerror = () => reject(new Error("audio"));
      setTimeout(() => reject(new Error("timeout")), 5000);
    });
    return Number.isFinite(audio.duration)
      ? Math.round(audio.duration * 1000)
      : null;
  } catch {
    return null;
  }
}

type PdfJs = typeof import("pdfjs-dist");
let pdfjs: Promise<PdfJs> | null = null;

/** pdf.js is loaded only when a PDF is previewed. */
function loadPdfJs(): Promise<PdfJs> {
  pdfjs ??= Promise.all([
    import("pdfjs-dist"),
    import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
  ]).then(([lib, worker]) => {
    lib.GlobalWorkerOptions.workerSrc = (worker as { default: string }).default;
    return lib;
  });
  return pdfjs;
}

/** The text of a PDF, page by page, read in the browser so the server never parses the file. */
export async function pdfText(
  source: Blob,
  maxChars = 500_000,
): Promise<{ pages: number; text: string; truncated: boolean }> {
  const lib = await loadPdfJs();
  const task = lib.getDocument({
    data: new Uint8Array(await source.arrayBuffer()),
  });
  const doc = await task.promise;
  try {
    const parts: string[] = [];
    let size = 0;
    for (let i = 1; i <= doc.numPages && size < maxChars; i += 1) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      const text = content.items
        .map((item) =>
          "str" in item ? item.str + (item.hasEOL ? "\n" : "") : "",
        )
        .join("")
        .replace(/[ \t]+/g, " ")
        .trim();
      if (text) {
        parts.push(text);
        size += text.length;
      }
    }
    const text = parts.join("\n\n");
    return {
      pages: doc.numPages,
      text: text.slice(0, maxChars),
      truncated: text.length > maxChars,
    };
  } finally {
    await task.destroy();
  }
}

/** Page count and a rendering of the first page, as WhatsApp Web shows before sending a PDF. */
export async function pdfPreview(
  source: Blob,
  width = 640,
): Promise<{ pages: number; url: string | null }> {
  const lib = await loadPdfJs();
  const task = lib.getDocument({
    data: new Uint8Array(await source.arrayBuffer()),
  });
  const doc = await task.promise;
  try {
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: width / base.width });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    await page.render({ canvas, viewport }).promise;
    const blob = await toBlob(canvas, "image/jpeg", 0.85);
    return {
      pages: doc.numPages,
      url: blob ? URL.createObjectURL(blob) : null,
    };
  } finally {
    await task.destroy();
  }
}

function blank(
  file: File,
  intent: UploadIntent,
  plan: UploadPlan,
): PreparedFile {
  return {
    key: nextKey(),
    intent,
    original: file,
    file: null,
    plan,
    kind: plan.ok ? plan.kind : null,
    status: "preparing",
    error: null,
    note: null,
    previewUrl: null,
    thumbUrl: null,
    pages: null,
    width: null,
    height: null,
    durationMs: null,
    caption: "",
  };
}

/** A new entry for the composer, still preparing; `prepare` fills it in place. */
export function createPrepared(file: File, intent: UploadIntent): PreparedFile {
  return blank(file, intent, planUpload(file, intent));
}

/** Runs conversions and previews for one entry, mutating it (it lives in a reactive array). */
export async function prepare(p: PreparedFile): Promise<PreparedFile> {
  const plan = p.plan;
  const file = p.original;
  const mime = resolveMime(file);
  try {
    if (!plan.ok) {
      p.error = {
        code: plan.reason,
        ...(plan.maxBytes ? { maxBytes: plan.maxBytes } : {}),
      };
      p.status = "error";
      if (
        mime.startsWith("image/") &&
        !HEIF_IMAGES.has(mime) &&
        file.size <= 50 * 1024 * 1024
      ) {
        p.previewUrl = URL.createObjectURL(file);
        p.thumbUrl = p.previewUrl;
      }
      return p;
    }
    switch (plan.conversion) {
      case "image_to_jpeg":
      case "heif_to_jpeg":
      case "image_compress": {
        let converted;
        try {
          converted = await toJpeg(file);
        } catch {
          p.error = {
            code:
              plan.conversion === "heif_to_jpeg"
                ? "heic_browser"
                : "conversion_failed",
          };
          p.status = "error";
          return p;
        }
        p.file = new File([converted.blob], renamed(file.name, "jpg"), {
          type: "image/jpeg",
        });
        p.width = converted.width;
        p.height = converted.height;
        p.note =
          plan.conversion === "image_compress"
            ? "compressed"
            : "converted_jpeg";
        p.previewUrl = URL.createObjectURL(p.file);
        p.thumbUrl = p.previewUrl;
        break;
      }
      case "sticker_webp": {
        const blob = await toSticker(file);
        p.file = new File([blob], renamed(file.name, "webp"), {
          type: "image/webp",
        });
        p.note = "sticker";
        p.previewUrl = URL.createObjectURL(p.file);
        p.thumbUrl = p.previewUrl;
        p.width = STICKER_SIZE;
        p.height = STICKER_SIZE;
        break;
      }
      default: {
        p.file = file;
        if (plan.conversion === "text_as_plain") p.note = "as_text";
        if (
          plan.kind === "image" ||
          (plan.kind === "document" && mime.startsWith("image/"))
        ) {
          p.previewUrl = URL.createObjectURL(file);
          p.thumbUrl = p.previewUrl;
          try {
            const bitmap = await createImageBitmap(file);
            p.width = bitmap.width;
            p.height = bitmap.height;
            bitmap.close();
          } catch {
            /* dimensions are optional */
          }
        } else if (mime.startsWith("video/")) {
          p.previewUrl = URL.createObjectURL(file);
          const frame = await videoFrame(p.previewUrl);
          p.thumbUrl = frame.thumb;
          p.width = frame.width || null;
          p.height = frame.height || null;
          p.durationMs = frame.durationMs;
        } else if (mime.startsWith("audio/")) {
          p.previewUrl = URL.createObjectURL(file);
          p.durationMs = await audioDuration(p.previewUrl);
        } else if (mime === "application/pdf") {
          await pdfInto(p, file);
        }
      }
    }
    p.status = "ready";
  } catch {
    p.error = { code: "conversion_failed" };
    p.status = "error";
  }
  return p;
}

async function pdfInto(p: PreparedFile, file: Blob): Promise<void> {
  try {
    const preview = await pdfPreview(file);
    p.pages = preview.pages;
    p.previewUrl = preview.url;
    p.thumbUrl = preview.url;
  } catch {
    /* a PDF that pdf.js cannot open still goes as a document */
  }
}

/** Revokes the object URLs an entry created. */
export function release(p: PreparedFile): void {
  const urls = new Set([p.previewUrl, p.thumbUrl].filter(Boolean) as string[]);
  urls.forEach((u) => URL.revokeObjectURL(u));
}

const TONES: Array<[RegExp, string]> = [
  [/^pdf$/, "#e5252a"],
  [/^(doc|docx|odt|rtf|pages)$/, "#2b579a"],
  [/^(xls|xlsx|csv|tsv|ods|numbers)$/, "#1f7244"],
  [/^(ppt|pptx|odp|key)$/, "#d24726"],
  [/^(zip|rar|7z|tar|gz)$/, "#8a6a2f"],
  [/^(mp3|m4a|aac|ogg|oga|opus|amr|wav|aif|aiff|flac|caf)$/, "#f08a24"],
  [/^(mp4|mov|m4v|webm|mkv|avi|3gp)$/, "#7a57d1"],
  [/^(jpg|jpeg|png|webp|gif|bmp|heic|heif|avif|svg|tif|tiff)$/, "#0f9d8a"],
];

/** Colour of the file-type tile, close to the icons WhatsApp shows per extension. */
export function fileTone(name: string): string {
  const ext = extensionOf(name);
  return TONES.find(([re]) => re.test(ext))?.[1] ?? "#667781";
}

/** Upper-case extension WhatsApp shows next to the size ("PDF", "ZIP", "HEIC"). */
export function extensionLabel(name: string): string {
  return extensionOf(name).toUpperCase() || "ARQUIVO";
}

/** `accept` attribute for each attach menu entry. Documents accept anything. */
export function acceptFor(intent: UploadIntent): string {
  switch (intent) {
    case "media":
      return "image/*,video/*";
    case "audio":
      return "audio/*";
    case "sticker":
      return "image/*";
    default:
      return "";
  }
}
